import { ICellRenderer } from "ag-grid-community";
import type MetricData from 'src/AngularJS/Models/metric-data';

const ARROW_UP = '\u2191';
const ARROW_DOWN = '\u2193';

export default class MetricAnimateShowChangeCellRenderer implements ICellRenderer {

    private static TEMPLATE =
        '<span>' +
        '<span class="ag-value-change-delta"></span>' +
        '<span class="ag-value-change-value"></span>' +
        '</span>';

    private lastValue: MetricData;
    private eGui: HTMLElement;
    private eValue: HTMLElement;
    private eDelta: HTMLElement;
    private refreshCount: number;

    public init(params: any): void {
        // initialize template
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = MetricAnimateShowChangeCellRenderer.TEMPLATE;
        this.eGui = tempDiv.firstChild as HTMLElement;
        this.eGui['__agComponent'] = this;

        // child elements
        this.eValue = this.eGui.querySelector('.ag-value-change-value');
        this.eDelta = this.eGui.querySelector('.ag-value-change-delta');

        this.refreshCount = 0;
        this.refresh(params);
    }

    public getGui() {
        return this.eGui;
    }

    private showDelta(params: any, delta: number) {
        const absDelta = Math.abs(delta);
        const valueFormatted = params.formatValue(absDelta);
        const valueToUse = (valueFormatted != null && valueFormatted !== '') ? valueFormatted : absDelta;
        const deltaUp = (delta >= 0);

        if (deltaUp) {
            this.eDelta.innerHTML = ARROW_UP + valueToUse;
        } else {
            this.eDelta.innerHTML = ARROW_DOWN + valueToUse;
        }

        this.eDelta.classList.toggle('ag-value-change-delta-up', deltaUp);
        this.eDelta.classList.toggle('ag-value-change-delta-down', !deltaUp);
    }

    private setTimerToRemoveDelta(): void {
        // the refreshCount makes sure that if the value updates again while
        // the below timer is waiting, then the below timer will realise it
        // is not the most recent and will not try to remove the delta value.
        this.refreshCount++;
        const refreshCountCopy = this.refreshCount;
        window.setTimeout(() => {
            if (refreshCountCopy === this.refreshCount) {
                this.hideDeltaValue();
            }
        }, 2000);
    }

    private clearElement(el: HTMLElement) {
        while (el && el.firstChild)
            el.removeChild(el.firstChild);
    }

    private hideDeltaValue(): void {
        this.eValue.classList.remove('ag-value-change-value-highlight');
        this.clearElement(this.eDelta);
    }

    public refresh(params: any): boolean {
        const value = params.value;

        if (value === this.lastValue) {
            return;
        }

        if (params.valueFormatted != null) {
            this.eValue.innerHTML = params.valueFormatted;
        } else if (params.value != null) {
            this.eValue.innerHTML = value;
        } else {
            this.clearElement(this.eValue);
        }

        if (value?.MetricValue != null && this.lastValue?.MetricValue != null) {
            const delta = value.MetricValue - this.lastValue.MetricValue;
            this.showDelta(params, delta);
        }

        // highlight the current value, but only if it's not new, otherwise it
        // would get highlighted first time the value is shown
        if (this.lastValue) {
            this.eValue.classList.add('ag-value-change-value-highlight');
        }

        this.setTimerToRemoveDelta();
        this.lastValue = value;
        return true;
    }
}
